import {
  Button,
  Card,
  CardBody,
  Flex,
  IconButton,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import {
  suppliersApi,
  SupplierProductGroupDto,
} from '@texas/api/endpoints/suppliersApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { BodyOverride } from '@texas/components/shared/BodyOverride';
import { DataTableContainer } from '@texas/components/shared/dataTable/DataTableContainer';
import { defaultIconSize, Icons } from '@texas/components/shared/Icons';
import { VerifyDialogWithRequest } from '@texas/components/shared/dialog/VerifyDialogWithRequest';
import { useValueDisclosure } from '@texas/hooks/useValueDisclosure';
import { createColumnHelper } from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AddProductGroupsModal } from './AddProductGroupsModal';
import { EditProductGroupModal } from './EditProductGroupModal';
import { TexasHeader } from '@texas/components/shared/TexasHeader';

interface Props {
  supplierId: number;
}

const columnHelper = createColumnHelper<SupplierProductGroupDto>();

export function SupplierProductGroups({ supplierId }: Props) {
  const { t } = useTranslation();
  const toast = useToast();

  const { data, refetch, loading, error, set } = useApiResource(
    suppliersApi.productGroups.get,
  );

  const [sorting, setSorting] = useState<{ key: string; desc: boolean }>({
    key: 'productGroup',
    desc: false,
  });

  const {
    isOpen: isAddOpen,
    onOpen: onAddOpen,
    onClose: onAddClose,
  } = useDisclosure();

  const {
    value: editValue,
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose,
  } = useValueDisclosure<SupplierProductGroupDto>();

  const {
    value: removeValue,
    isOpen: isRemoveOpen,
    onOpen: onRemoveOpen,
    onClose: onRemoveClose,
  } = useValueDisclosure<SupplierProductGroupDto>();

  useEffect(() => {
    refetch(supplierId, sorting.key, sorting.desc);
  }, [refetch, supplierId, sorting.key, sorting.desc]);

  const columns = useMemo(
    () => [
      columnHelper.accessor((row) => `${row.value} (${row.code})`, {
        id: 'productGroup',
        header: t('general.productGroup'),
        cell: (info) => <Text fontWeight="medium">{info.getValue()}</Text>,
      }),
      columnHelper.accessor('articleCount', {
        id: 'articleCount',
        header: t('suppliers.articleCount'),
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('sampleLeadTime', {
        id: 'sampleLeadTime',
        header: t('suppliers.sampleLeadTime'),
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('actualSampleLeadTime', {
        id: 'actualSampleLeadTime',
        header: t('suppliers.actualSampleLeadTime'),
        cell: (info) => {
          const val = info.getValue();
          return val !== null ? Math.round(val) : '';
        },
      }),
      columnHelper.accessor('bulkLeadTime', {
        id: 'bulkLeadTime',
        header: t('suppliers.bulkLeadTime'),
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor('shipmentLeadTime', {
        id: 'shipmentLeadTime',
        header: t('suppliers.shipmentLeadTime'),
        cell: (info) => info.getValue(),
      }),
      columnHelper.display({
        id: 'actions',
        enableSorting: false,
        cell: (info) => (
          <Flex
            gap={1}
            justify="flex-end"
            opacity={0}
            transition="opacity 0.15s"
            sx={{ 'tr:hover &': { opacity: 1 } }}
          >
            <Tooltip label={t('suppliers.editProductGroup')}>
              <IconButton
                aria-label={t('suppliers.editProductGroup')}
                icon={<Icons.Pencil boxSize={defaultIconSize} />}
                variant="ghost"
                size="sm"
                onClick={() => onEditOpen(info.row.original)}
              />
            </Tooltip>
            <Tooltip label={t('suppliers.removeProductGroup')}>
              <IconButton
                aria-label={t('suppliers.removeProductGroup')}
                icon={<Icons.TrashOutline boxSize={defaultIconSize} />}
                variant="ghost"
                size="sm"
                colorScheme="red"
                onClick={() => onRemoveOpen(info.row.original)}
              />
            </Tooltip>
          </Flex>
        ),
        meta: { stopCellPropagation: true },
      }),
    ],
    [t, onEditOpen, onRemoveOpen],
  );

  return (
    <BodyOverride>
      <Card variant="dark">
        <CardBody>
          <Flex justify="space-between" mb={4}>
            <TexasHeader text={t('general.productGroups')} />
            <Button variant="texas-solid" onClick={onAddOpen}>
              {t('suppliers.addProductGroups')}
            </Button>
          </Flex>

          <DataTableContainer
            w="100%"
            error={error}
            datatable={{
              data: data ?? [],
              columns,
              isLoading: loading,
              variant: 'simple',
              rowProps: {
                getRowId: (row) => String(row.productGroupId),
              },
              sorted: {
                key: sorting.key,
                desc: sorting.desc,
                onSortedChange: (s) => setSorting(s),
              },
            }}
          />

          <AddProductGroupsModal
            supplierId={supplierId}
            existingProductGroupIds={data?.map((x) => x.productGroupId) ?? []}
            isOpen={isAddOpen}
            onClose={onAddClose}
            onAdded={(updated) => {
              set(updated);
              onAddClose();
              toast({
                title: t('general.added'),
                status: 'success',
                isClosable: true,
              });
            }}
          />

          {editValue && (
            <EditProductGroupModal
              supplierId={supplierId}
              productGroup={editValue}
              isOpen={isEditOpen}
              onClose={onEditClose}
              onUpdated={(updated) => {
                set((prev) =>
                  (prev ?? []).map((pg) =>
                    pg.productGroupId === updated.productGroupId ? updated : pg,
                  ),
                );
                onEditClose();
              }}
            />
          )}

          {removeValue && (
            <VerifyDialogWithRequest
              headerTitle={t('suppliers.removeProductGroup')}
              secondaryButtonTitle={t('general.cancel')}
              primaryButtonTitle={t('general.confirm')}
              primaryRequest={suppliersApi.productGroups.remove}
              args={[supplierId, removeValue.productGroupId]}
              isOpen={isRemoveOpen}
              onClose={onRemoveClose}
              onPerformed={() => {
                set((prev) =>
                  (prev ?? []).filter(
                    (pg) => pg.productGroupId !== removeValue.productGroupId,
                  ),
                );
                onRemoveClose();
              }}
              onSuccessTitle={t('general.removed')}
            >
              {t('suppliers.removeProductGroupConfirm')}
            </VerifyDialogWithRequest>
          )}
        </CardBody>
      </Card>
    </BodyOverride>
  );
}
